import { useRef, useState, useEffect, useLayoutEffect } from 'preact/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import YouTubeVideo from '../shared-components/video/YouTubeVideo.component';
import Loading from '../Loading/Loading.component';
import WinnerIcon from '../shared-components/icons/WinnerIcon';
import ThumbIcon from '../shared-components/icons/ThumbIcon';
import ChevronLeftIcon from '../../assets/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../assets/icons/ChevronRigthIcon';

import styleSheet from './poll.module.scss';

const styles = styleSheet.locals || {};
// must match the track's CSS gap
const CARD_GAP = 4;

/**
 * Options list for polls where each option has its own video (poll_video_type
 * === 'option'). Renders a carousel of cards (facade video + label + vote
 * button / result bar). Only one video loads at a time.
 *
 * @param {object} props - component props
 * @param {Array} props.options - options to render (with percentage/winner when showing results)
 * @param {Function} props.onSelect - selects an option
 * @param {Function} [props.onVoteSubmit] - submits the vote once a card is picked
 * @param {Array} props.selectedOptions - currently selected options
 * @param {boolean} props.showingResults - render result bars instead of vote buttons
 * @param {string|Array} props.pollUserAnswer - the fan's existing answer
 * @param {boolean} props.showVotesCounter - show the vote count with the percentage
 * @param {boolean} props.submittingVote - a vote is in flight (show loading, block re-votes)
 * @param {object} props.scrollStateRef - ref persisting the scroll position across vote → results
 * @param {boolean} props.resultsHidden - fan voted but results are withheld; show the hidden-results box
 * @param {string} props.resultsHiddenText - copy shown inside the hidden-results box
 * @returns {Function} <PollVideoOptionsList />
 */
function PollVideoOptionsList({
    options,
    onSelect,
    onVoteSubmit,
    selectedOptions,
    showingResults,
    pollUserAnswer,
    showVotesCounter,
    submittingVote = false,
    scrollStateRef,
    resultsHidden = false,
    resultsHiddenText = ''
}) {
    const carouselRef = useRef(null);
    // only one option video is loaded at a time
    const [activeVideoId, setActiveVideoId] = useState(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [hasOverflow, setHasOverflow] = useState(true);
    const [cardsPerView, setCardsPerView] = useState(1);

    useLayoutEffect(() => {
        const track = carouselRef.current;
        if (!track) {
            return null;
        }
        const measure = () => {
            setHasOverflow(track.scrollWidth > track.clientWidth + 1);
            const card = track.children[0];
            if (card) {
                setCardsPerView(
                    Math.max(
                        1,
                        Math.round(
                            track.clientWidth / (card.offsetWidth + CARD_GAP)
                        )
                    )
                );
            }
        };
        measure();

        if (typeof ResizeObserver === 'function') {
            const observer = new ResizeObserver(measure);
            observer.observe(track);
            return () => observer.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [options.length]);

    // "Place vote" selects, then submits once the selection state has settled
    const [pendingSubmit, setPendingSubmit] = useState(false);
    useEffect(() => {
        if (pendingSubmit && selectedOptions?.length > 0) {
            setPendingSubmit(false);
            onVoteSubmit?.();
        }
    }, [pendingSubmit, selectedOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    const onVote = (option) => {
        if (submittingVote) {
            return;
        }
        if (scrollStateRef) {
            scrollStateRef.current = carouselRef.current?.scrollLeft ?? 0;
        }
        onSelect(option);
        setPendingSubmit(true);
    };

    const scrollByPage = (direction) => {
        const track = carouselRef.current;
        const card = track?.children[0];
        if (!track || !card) {
            return;
        }
        track.scrollBy({
            left: (card.offsetWidth + CARD_GAP) * cardsPerView * direction,
            behavior: 'smooth'
        });
    };

    const pageCount = Math.max(1, Math.ceil(options.length / cardsPerView));

    const onTrackScroll = () => {
        const track = carouselRef.current;
        const card = track?.children[0];
        if (!track || !card) {
            return;
        }
        if (scrollStateRef) {
            scrollStateRef.current = track.scrollLeft;
        }
        const atEnd =
            track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        const pageWidth = (card.offsetWidth + CARD_GAP) * cardsPerView;
        setSlideIndex(
            atEnd ? pageCount - 1 : Math.round(track.scrollLeft / pageWidth)
        );

        // stop the playing video once its card is scrolled out of view
        if (activeVideoId !== null) {
            const activeIndex = options.findIndex(
                (opt) => opt.option_id === activeVideoId
            );
            const activeCard = track.children[activeIndex];
            if (activeCard) {
                const left = activeCard.offsetLeft - track.scrollLeft;
                const outOfView =
                    left + activeCard.offsetWidth <= 0 ||
                    left >= track.clientWidth;
                if (outOfView) {
                    setActiveVideoId(null);
                }
            }
        }
    };

    const isAnsweredOption = (option) => {
        const answerIds = Array.isArray(pollUserAnswer)
            ? pollUserAnswer
            : typeof pollUserAnswer === 'string'
              ? pollUserAnswer.split(',')
              : [];
        return answerIds.some((id) => String(id) === String(option.option_id));
    };

    // The results screen remounts the carousel: restore the exact scroll
    // position the fan voted from, or (fresh page load) jump to the page
    // holding their answer so the card sits in its natural slot.
    useLayoutEffect(() => {
        const track = carouselRef.current;
        const card = track?.children[0];
        if (!track || !card) {
            return;
        }

        let left = scrollStateRef?.current;
        if (typeof left !== 'number') {
            const answeredIndex = options.findIndex(isAnsweredOption);
            if (answeredIndex <= 0) {
                return;
            }
            const stride = card.offsetWidth + CARD_GAP;
            // cardsPerView state isn't measured yet on first paint
            const perView = Math.max(1, Math.round(track.clientWidth / stride));
            left = Math.floor(answeredIndex / perView) * perView * stride;
        }
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = left;
        track.style.scrollBehavior = '';
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div data-cy="optionCarousel" className={styles.pollVideoOptions}>
            <div
                data-cy="videoCarousel"
                className={styles.pollVideoOptionsTrack}
                ref={carouselRef}
                onScroll={onTrackScroll}
            >
                {options.map((option) => {
                    const isSelected = Boolean(
                        selectedOptions?.some(
                            (opt) => opt.option_id === option.option_id
                        )
                    );
                    const isAnswered = isAnsweredOption(option);

                    return (
                        <div
                            data-cy="singleOption"
                            className={classNames(styles.pollVideoOptionCard, {
                                [styles.pollVideoOptionCardSelected]:
                                    isSelected || isAnswered
                            })}
                            key={option.option_id || option.text}
                        >
                            <div
                                data-cy="youtubeVideo"
                                className={styles.pollVideoOptionMedia}
                            >
                                <YouTubeVideo
                                    videoId={option.youtube_video_id}
                                    title={option.text}
                                    thumbnailQuality="hqdefault"
                                    activated={
                                        activeVideoId === option.option_id
                                    }
                                    onActivate={() =>
                                        setActiveVideoId(option.option_id)
                                    }
                                />

                                {showingResults && option.winner && (
                                    <span
                                        className={styles.pollVideoOptionWinner}
                                        data-cy="video-option-winner"
                                    >
                                        <WinnerIcon />
                                    </span>
                                )}
                            </div>

                            <div className={styles.pollVideoOptionBody}>
                                <div
                                    data-cy="videoOptionThumb"
                                    className={styles.pollVideoOptionLabelRow}
                                >
                                    {isAnswered && (
                                        <span
                                            className={
                                                styles.pollVideoOptionThumb
                                            }
                                        >
                                            <ThumbIcon />
                                        </span>
                                    )}
                                    <p
                                        data-cy="videoOptionLabel"
                                        className={styles.pollVideoOptionLabel}
                                    >
                                        {option.text}
                                    </p>
                                </div>

                                {resultsHidden ? (
                                    <div
                                        className={
                                            styles.pollVideoOptionHiddenResults
                                        }
                                        data-cy="video-option-hidden-results"
                                    >
                                        {resultsHiddenText}
                                    </div>
                                ) : showingResults ? (
                                    <div
                                        data-cy="optionResult"
                                        className={styles.pollVideoOptionResult}
                                    >
                                        <span
                                            className={
                                                styles.pollVideoOptionResultBar
                                            }
                                            style={{
                                                width: `${option.percentage || 0}%`
                                            }}
                                            data-cy="resultBar"
                                        />
                                        <span
                                            data-cy="resultText"
                                            className={
                                                styles.pollVideoOptionResultText
                                            }
                                        >
                                            <strong>
                                                {option.percentage || 0}%
                                                Selected
                                            </strong>
                                            {showVotesCounter && (
                                                <span>
                                                    {' '}
                                                    {option.times_selected}{' '}
                                                    votes
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className={
                                            styles.pollVideoOptionVoteButton
                                        }
                                        onClick={() => onVote(option)}
                                        disabled={submittingVote}
                                        data-cy="video-option-vote"
                                    >
                                        {submittingVote && isSelected ? (
                                            <Loading size={14} />
                                        ) : (
                                            'Place vote'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {hasOverflow && (
                <div
                    data-cy="optionControls"
                    className={styles.pollVideoOptionsControls}
                >
                    <ol className={styles.pollVideoOptionsDots}>
                        {Array.from({ length: pageCount }, (unused, index) => (
                            <li
                                key={index}
                                className={classNames(
                                    styles.pollVideoOptionsDot,
                                    {
                                        [styles.pollVideoOptionsDotActive]:
                                            index === slideIndex
                                    }
                                )}
                            />
                        ))}
                    </ol>

                    <div
                        data-cy="optionArrows"
                        className={styles.pollVideoOptionsArrows}
                    >
                        <button
                            data-cy="previousOption"
                            type="button"
                            className={styles.pollVideoOptionsArrow}
                            onClick={() => scrollByPage(-1)}
                            disabled={slideIndex === 0}
                            aria-label="Previous option"
                        >
                            <ChevronLeftIcon />
                        </button>
                        <button
                            data-cy="nextOption"
                            type="button"
                            className={styles.pollVideoOptionsArrow}
                            onClick={() => scrollByPage(1)}
                            disabled={slideIndex >= pageCount - 1}
                            aria-label="Next option"
                        >
                            <ChevronRightIcon />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

PollVideoOptionsList.propTypes = {
    options: PropTypes.array.isRequired,
    onSelect: PropTypes.func.isRequired,
    onVoteSubmit: PropTypes.func,
    selectedOptions: PropTypes.array,
    showingResults: PropTypes.bool,
    pollUserAnswer: PropTypes.any,
    showVotesCounter: PropTypes.bool,
    submittingVote: PropTypes.bool,
    scrollStateRef: PropTypes.object,
    resultsHidden: PropTypes.bool,
    resultsHiddenText: PropTypes.string
};

export default PollVideoOptionsList;
